import { Fragment, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, type Client } from '../lib/api'
import { PageHead, Skel, Empty, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

type TicketStatus = 'open' | 'waiting' | 'resolved' | 'closed'
type TicketPriority = 'low' | 'normal' | 'high' | 'urgent'
type SlaState = 'ok' | 'due_soon' | 'breached'

interface Ticket {
  id: string
  client_id?: string
  case_id?: string
  subject: string
  body: string
  priority: TicketPriority
  status: TicketStatus
  sla_due_at: string
  first_response_at?: string
  resolved_at?: string
  assignee_id?: string
  created_at: string
  updated_at: string
  sla_state: SlaState
}

interface TicketStats {
  open: number
  breached: number
  avg_first_response_hours: number
}

const statusLabel: Record<TicketStatus, string> = {
  open: 'เปิดอยู่', waiting: 'รอลูกค้า', resolved: 'แก้ไขแล้ว', closed: 'ปิดแล้ว',
}
const priorityLabel: Record<TicketPriority, string> = {
  low: 'ต่ำ', normal: 'ปกติ', high: 'สูง', urgent: 'เร่งด่วน',
}
const filters: { value: TicketStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'ทั้งหมด' },
  { value: 'open', label: 'เปิดอยู่' },
  { value: 'waiting', label: 'รอลูกค้า' },
  { value: 'resolved', label: 'แก้ไขแล้ว' },
  { value: 'closed', label: 'ปิดแล้ว' },
]

const nextSteps: Record<TicketStatus, { to: TicketStatus; label: string; primary?: boolean }[]> = {
  open: [{ to: 'waiting', label: 'รอลูกค้า' }, { to: 'resolved', label: 'แก้ไขแล้ว', primary: true }],
  waiting: [{ to: 'open', label: 'กลับไปเปิด' }, { to: 'resolved', label: 'แก้ไขแล้ว', primary: true }],
  resolved: [{ to: 'open', label: 'เปิดใหม่' }, { to: 'closed', label: 'ปิดตั๋ว', primary: true }],
  closed: [{ to: 'open', label: 'เปิดใหม่' }],
}

/** Hours and minutes until the promise breaks, or since it broke. */
function remaining(iso: string, now: number): string {
  const ms = new Date(iso).getTime() - now
  const abs = Math.abs(ms)
  const days = Math.floor(abs / 86_400_000)
  const hours = Math.floor((abs % 86_400_000) / 3_600_000)
  const mins = Math.floor((abs % 3_600_000) / 60_000)
  const span = days > 0 ? `${days} วัน ${hours} ชม.` : `${hours} ชม. ${mins} น.`
  return ms < 0 ? `เกิน ${span}` : `เหลือ ${span}`
}

function SlaCell({ t, now }: { t: Ticket; now: number }) {
  const frozen = t.status === 'resolved' || t.status === 'closed'
  const tone = t.sla_state === 'breached' ? 'sev-critical' : t.sla_state === 'due_soon' ? 'sev-high' : 'dim'
  return (
    <div>
      {frozen ? (
        <span className="dim">{t.first_response_at ? 'ตอบแล้ว' : 'หยุดเวลาแล้ว'}</span>
      ) : (
        <span className={`sev ${tone}`}>
          <i className="sev-dot" />
          {remaining(t.sla_due_at, now)}
        </span>
      )}
      <div className="dim tnum" style={{ fontSize: 11, marginTop: 4 }}>กำหนด {thaiDateTime(t.sla_due_at)}</div>
    </div>
  )
}

export function Support() {
  const qc = useQueryClient()
  const [filter, setFilter] = useState<TicketStatus | 'all'>('all')
  const [open, setOpen] = useState<string | null>(null)
  const [composing, setComposing] = useState(false)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [priority, setPriority] = useState<TicketPriority>('normal')
  const [clientId, setClientId] = useState('')
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(timer)
  }, [])

  const tickets = useQuery({
    queryKey: ['tickets', filter],
    queryFn: () => api<Ticket[]>(`/v1/tickets${filter === 'all' ? '' : `?status=${filter}`}`),
    staleTime: 15_000,
  })
  const stats = useQuery({
    queryKey: ['ticket-stats'],
    queryFn: () => api<TicketStats>('/v1/tickets/stats'),
    staleTime: 15_000,
  })
  const clients = useQuery({
    queryKey: ['clients'],
    queryFn: () => api<Client[]>('/v1/clients'),
    staleTime: 60_000,
  })

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['tickets'] })
    void qc.invalidateQueries({ queryKey: ['ticket-stats'] })
  }

  const move = useMutation({
    mutationFn: ({ id, status }: { id: string; status: TicketStatus }) =>
      apiWrite<Ticket>(`/v1/tickets/${id}`, 'PATCH', { status }),
    onSuccess: refresh,
  })
  const create = useMutation({
    mutationFn: () => apiWrite<Ticket>('/v1/tickets', 'POST', {
      subject, body, priority, client_id: clientId,
    }),
    onSuccess: () => {
      setComposing(false)
      setSubject('')
      setBody('')
      setPriority('normal')
      setClientId('')
      refresh()
    },
  })

  const clientName = useMemo(() => {
    const map = new Map((clients.data ?? []).map((c) => [c.id, c.name]))
    return (id?: string) => (id ? map.get(id) ?? '—' : '—')
  }, [clients.data])

  const rows = tickets.data ?? []
  const s = stats.data

  return (
    <>
      <PageHead
        title="ดูแลลูกค้า"
        sub="ตั๋วบริการที่มีสัญญาตอบกลับ · เวลาหยุดที่การตอบครั้งแรก ไม่ใช่การปิดเรื่อง"
        actions={
          <button className="btn btn-primary" onClick={() => setComposing((v) => !v)}>
            {composing ? 'ยกเลิก' : 'เปิดตั๋วใหม่'}
          </button>
        }
      />

      <div className="grid-3" style={{ marginBottom: 16 }}>
        <Stat label="เปิดอยู่" value={s ? s.open : '—'} unit="ใบ" foot="รวมที่รอลูกค้าตอบ" />
        <Stat
          label="เกิน SLA"
          value={<span style={{ color: s && s.breached > 0 ? 'var(--color-crimson)' : undefined }}>{s ? s.breached : '—'}</span>}
          unit="ใบ"
          foot="ยังไม่ตอบและเลยกำหนดแล้ว"
        />
        <Stat label="ตอบครั้งแรกเฉลี่ย" value={s ? s.avg_first_response_hours.toFixed(1) : '—'} unit="ชม." foot="นับจากเวลาเปิดตั๋ว" />
      </div>

      {composing && (
        <div className="card card-pad rise" style={{ marginBottom: 16 }}>
          <div className="grid-2">
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="ticket-subject">หัวข้อเรื่อง</label>
              <input id="ticket-subject" className="input" style={{ width: '100%' }} maxLength={200}
                value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="เช่น ระบบยืนยันยอดไม่ขึ้น" />
            </div>
            <div className="field" style={{ marginTop: 0 }}>
              <label htmlFor="ticket-priority">ระดับความสำคัญ</label>
              <select id="ticket-priority" className="select" style={{ width: '100%' }}
                value={priority} onChange={(e) => setPriority(e.target.value as TicketPriority)}>
                <option value="low">ต่ำ · ตอบภายใน 72 ชม.</option>
                <option value="normal">ปกติ · ตอบภายใน 48 ชม.</option>
                <option value="high">สูง · ตอบภายใน 8 ชม.</option>
                <option value="urgent">เร่งด่วน · ตอบภายใน 4 ชม.</option>
              </select>
            </div>
          </div>
          <div className="field">
            <label htmlFor="ticket-body">รายละเอียด</label>
            <textarea id="ticket-body" className="input" rows={4} style={{ width: '100%', height: 'auto', padding: '10px 12px' }}
              maxLength={20000} value={body} onChange={(e) => setBody(e.target.value)} placeholder="สิ่งที่ลูกค้าแจ้งและสิ่งที่ตรวจพบ" />
          </div>
          <div className="row" style={{ marginTop: 14, gap: 10 }}>
            <select className="select" value={clientId} onChange={(e) => setClientId(e.target.value)} style={{ minWidth: 220 }}>
              <option value="">ไม่ระบุลูกค้า</option>
              {(clients.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button className="btn btn-primary" disabled={create.isPending || !subject.trim() || !body.trim()} onClick={() => create.mutate()}>
              บันทึกตั๋ว
            </button>
            {create.error && <span className="sev sev-critical"><i className="sev-dot" />{(create.error as Error).message}</span>}
          </div>
        </div>
      )}

      <div className="filters">
        {filters.map((f) => (
          <button key={f.value} className={`chip${filter === f.value ? ' chip-on' : ''}`} onClick={() => setFilter(f.value)}>
            {f.label}
          </button>
        ))}
      </div>

      {tickets.isLoading ? <Skel h={220} /> : rows.length === 0 ? (
        <Empty title="ยังไม่มีตั๋วในมุมมองนี้" body="เปิดตั๋วใหม่เมื่อลูกค้าแจ้งปัญหา แล้วระบบจะเริ่มนับ SLA ให้ทันที" />
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>เรื่อง</th><th>ลูกค้า</th><th>ความสำคัญ</th><th>SLA ตอบกลับ</th><th>สถานะ</th><th>เปิดเมื่อ</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <Fragment key={t.id}>
                  <tr onClick={() => setOpen(open === t.id ? null : t.id)}>
                    <td style={{ maxWidth: 380 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</div>
                      {t.case_id && <div className="mono dim" style={{ marginTop: 3 }}>เชื่อมกับเคสตรวจสอบ</div>}
                    </td>
                    <td className="dim">{clientName(t.client_id)}</td>
                    <td>
                      <span className={`chip${t.priority === 'urgent' || t.priority === 'high' ? ' gold' : ''}`}>
                        {priorityLabel[t.priority]}
                      </span>
                    </td>
                    <td><SlaCell t={t} now={now} /></td>
                    <td><span className="chip">{statusLabel[t.status]}</span></td>
                    <td className="dim">{thaiDateTime(t.created_at)}</td>
                  </tr>
                  {open === t.id && (
                    <tr key={`${t.id}-detail`}>
                      <td colSpan={6} style={{ background: 'var(--color-wash)' }}>
                        <p style={{ fontSize: 13, lineHeight: 1.75, whiteSpace: 'pre-wrap', margin: 0 }}>{t.body}</p>
                        <div className="dim row" style={{ gap: 18, marginTop: 10, fontSize: 11.5 }}>
                          <span>เปิดเมื่อ {thaiDateTime(t.created_at)}</span>
                          <span>{t.first_response_at ? `ตอบครั้งแรก ${thaiDateTime(t.first_response_at)}` : 'ยังไม่ตอบครั้งแรก'}</span>
                          {t.resolved_at && <span>แก้ไขเมื่อ {thaiDateTime(t.resolved_at)}</span>}
                        </div>
                        <div className="row" style={{ gap: 8, marginTop: 14 }}>
                          {nextSteps[t.status].map((step) => (
                            <button key={step.to} className={`btn btn-sm${step.primary ? ' btn-primary' : ''}`}
                              disabled={move.isPending}
                              onClick={() => move.mutate({ id: t.id, status: step.to })}>
                              {step.label}
                            </button>
                          ))}
                          {move.error && <span className="sev sev-critical"><i className="sev-dot" />{(move.error as Error).message}</span>}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
